import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { Td, Th } from "../components/eex/ui";
import { MarginPortfolio, MarginPortfolioSnapshot } from "../features/eex/margin/Portfolio";
import type {
  MarginContractDayStatus as ContractDayStatus,
  MarginLedgerContractDay as LedgerContractDay,
  MarginLedgerDay as LedgerDay,
  MarginResult,
} from "../features/eex/margin/contracts";

export const Route = createFileRoute("/eex/margin")({
  component: EexMarginPage,
  validateSearch: (search: Record<string, unknown>): { analysis: "portfolio" | "vm" } => ({ analysis: search.analysis === "vm" ? "vm" : "portfolio" }),
});

const eur = (v: number | null): string =>
  v == null ? "—" : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const px = (v: number | null, digits = 3): string => (v == null ? "—" : Number(v).toFixed(digits));

const STATUS_LABEL: Record<ContractDayStatus, string> = {
  match: "✓",
  mismatch: "✗ mismatch",
  app_only: "app only",
  bank_only: "bank only",
  no_price: "no price",
};

function statusClass(s: ContractDayStatus | "match" | "mismatch"): string {
  if (s === "match") return "text-posGreen";
  if (s === "mismatch") return "text-negRed font-semibold";
  return "text-muted";
}

function EexMarginPage() {
  const { analysis } = Route.useSearch();
  const navigate = Route.useNavigate();
  return <div className="margin-analysis">
    <header className="margin-heading"><div><h1>Margin</h1><p>Portfolio exposure and clearing cash flows</p></div>{analysis === "portfolio" && <MarginPortfolioSnapshot />}</header>
    <div role="tablist" aria-label="Margin analysis" className="margin-analysis-tabs">
      {(["portfolio", "vm"] as const).map(tab => (
        <button
          key={tab}
          id={`margin-tab-${tab}`}
          type="button"
          role="tab"
          aria-selected={analysis === tab}
          aria-controls={analysis === tab ? `margin-panel-${tab}` : undefined}
          tabIndex={analysis === tab ? 0 : -1}
          onKeyDown={event => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const next = event.key === "Home" ? "portfolio" : event.key === "End" ? "vm" : tab === "portfolio" ? "vm" : "portfolio";
            void navigate({ search: { analysis: next }, replace: true });
            document.getElementById(`margin-tab-${next}`)?.focus();
          }}
          className="margin-analysis-tab"
          onClick={() => void navigate({ search: { analysis: tab }, replace: true })}
        >
          {tab === "portfolio" ? "Portfolio" : "Variation margin"}
        </button>
      ))}
    </div>
    <div role="tabpanel" id={`margin-panel-${analysis}`} aria-labelledby={`margin-tab-${analysis}`}>{analysis === "portfolio" ? <MarginPortfolio /> : <VariationMargin />}</div>
  </div>;
}

function VariationMargin() {
  const marginQ = useQuery({
    queryKey: ["eex-margin"],
    queryFn: () => api<MarginResult>("/eex/margin"),
  });

  const data = marginQ.data;
  const days = data?.days ?? [];
  const matchedDays = days.filter((day) => day.status === "match").length;

  return (
    <div className="min-w-0 space-y-4 px-3 py-4 sm:space-y-6 sm:p-4">
      <header className="min-w-0">
        <h1 className="font-semibold text-lg">EEX Variation Margin</h1>
        <p className="text-muted text-xs max-w-3xl">
          Your trades replayed through ECC&rsquo;s daily settlement — carry VM on the prior position plus
          trade-day VM against each fill&rsquo;s price — reconciled contract-by-contract against the imported
          clearing statements. The ledger starts at the first imported statement; older trades only
          contribute their lots to the opening position.
        </p>
      </header>

      {marginQ.isLoading && <div className="text-muted text-sm">Computing…</div>}
      {marginQ.isError && <div className="text-negRed text-sm">{(marginQ.error as Error).message}</div>}

      {data && !data.span && (
        <div className="text-muted text-sm">
          No clearing statements imported yet — upload daily reports on the EEX Trades page first.
        </div>
      )}

      {data?.span && (
        <>
          <div className="grid min-w-0 grid-cols-1 gap-2 rounded-md border border-border/60 p-3 text-xs sm:flex sm:flex-wrap sm:gap-x-6 sm:gap-y-1">
            <span>
              <span className="text-muted">Statements:</span> {data.span.from} → {data.span.to} ({data.span.days} days)
            </span>
            <span>
              <span className="text-muted">Days reconciled:</span>{" "}
              <span className={matchedDays === days.length ? "text-posGreen" : "text-negRed"}>
                {matchedDays}/{days.length}
              </span>
            </span>
            <span>
              <span className="text-muted">Contracts tracked:</span> {data.contracts.length}
            </span>
            {data.pending && (
              <span className="text-muted">
                {data.pending.count} trade(s) / {data.pending.lots} MW after {data.pending.after} await the next statement
              </span>
            )}
          </div>

          <MarginSourceStatus span={data.span} settlementFeed={data.settlementFeed} />

          {data.exclusions.length > 0 && (
            <div className="min-w-0 space-y-1 rounded-md border border-border/60 p-3 text-xs">
              <div className="font-semibold">Not in the ledger</div>
              {data.exclusions.map((e) => (
                <div key={e.reason} className="break-words text-muted sm:break-normal">
                  {e.count} trade(s) / {e.lots} MW gross / {e.netLots > 0 ? "+" : ""}{e.netLots} MW net — {e.reason}
                  {e.contracts.length > 0 && (
                    <span> [{e.contracts.map((c) => `${c.label}: ${c.netLots > 0 ? "+" : ""}${c.netLots} MW`).join("; ")}]</span>
                  )}
                  {e.examples.length > 0 && <span className="break-all font-mono sm:break-normal"> (e.g. {e.examples.slice(0, 3).join("; ")})</span>}
                </div>
              ))}
            </div>
          )}

          {data.cascades.length > 0 && (
            <div className="min-w-0 space-y-1 rounded-md border border-border/60 p-3 text-xs">
              <div className="font-semibold">Cascades applied</div>
              {data.cascades.map((c, i) => (
                <div key={i} className={cn("break-words sm:break-normal", c.warning ? "text-negRed" : "text-muted")}>
                  {c.ltd}: <span className="italic">{c.parentLabel}</span> ({c.netQty > 0 ? "+" : ""}{c.netQty} MW)
                  {c.childLabels.length > 0 && (
                    <>
                      {" → "}
                      <span className="italic">{c.childLabels.join(" + ")}</span>
                      {c.parentPrice != null && <> @ {c.parentPrice}</>}
                      {c.twoStep && <span className="text-muted"> · two-step via front quarter</span>}
                    </>
                  )}
                  {c.warning && <> — {c.warning}</>}
                </div>
              ))}
            </div>
          )}

          <section className="space-y-1">
            <h2 className="text-xs font-semibold text-muted uppercase tracking-wide">Per contract (cumulative since {data.span.from})</h2>
            <div className="hidden overflow-x-auto rounded-md border border-border/60 sm:block">
              <table className="w-full text-xs">
                <thead className="bg-border/20 text-left">
                  <tr>
                    <Th>Contract</Th>
                    <Th className="text-right">Net pos (MW)</Th>
                    <Th className="text-right">App VM €</Th>
                    <Th className="text-right">Bank VM €</Th>
                    <Th className="text-right">Diff €</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.contracts.map((c) => (
                    <tr
                      key={`${c.code}-${c.expYear}-${c.expMonth}`}
                      className={cn("border-t border-border/40", c.expired && "opacity-60")}
                    >
                      <Td>
                        {c.label} <span className="font-mono text-muted">({c.code} {c.expYear}-{String(c.expMonth).padStart(2, "0")})</span>
                        {c.expired && <span className="text-muted italic"> expired</span>}
                      </Td>
                      <Td className="text-right tabular-nums font-semibold">{c.netPos}</Td>
                      <Td className={cn("text-right tabular-nums", c.cumComputedVm < 0 && "text-negRed")}>{eur(c.cumComputedVm)}</Td>
                      <Td className={cn("text-right tabular-nums", c.cumBankVm < 0 && "text-negRed")}>{eur(c.cumBankVm)}</Td>
                      <Td className={cn("text-right tabular-nums", Math.abs(c.diff) > 0.011 && "text-negRed font-semibold")}>{eur(c.diff)}</Td>
                      <Td className={statusClass(c.status)}>
                        {c.status === "match" ? "✓" : "✗"}
                        {c.incomplete && <span className="text-muted"> (gaps)</span>}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ContractCards contracts={data.contracts} />
          </section>

          <section className="space-y-1">
            <h2 className="text-xs font-semibold text-muted uppercase tracking-wide">Day by day</h2>
            <DaysTable days={days} />
          </section>
        </>
      )}
    </div>
  );
}

function MarginSourceStatus({
  span,
  settlementFeed,
}: {
  span: NonNullable<MarginResult["span"]>;
  settlementFeed: MarginResult["settlementFeed"];
}) {
  return (
    <details className="group min-w-0 rounded-md border border-border/60 text-xs sm:hidden">
      <summary className="flex min-h-11 cursor-pointer items-center gap-2 px-3 py-2 font-medium text-fg">
        <span aria-hidden="true" className="shrink-0 text-muted transition-transform group-open:rotate-90">▸</span>
        <span className="min-w-0 flex-1 break-words">Source status</span>
        <span className="shrink-0 text-muted">{settlementFeed.status}</span>
      </summary>
      <div className="grid min-w-0 gap-2 border-t border-border/40 p-3">
        <div className="min-w-0 rounded border border-border/40 p-2">
          <div className="font-semibold text-fg">Clearing statements</div>
          <div className="break-words text-muted">
            {span.days} day(s) · {span.from} → {span.to}
          </div>
        </div>
        <div className="min-w-0 rounded border border-border/40 p-2">
          <div className="font-semibold text-fg">ECC SPAN fallback</div>
          <div className="break-words text-muted">
            {settlementFeed.status} · {settlementFeed.reason}
          </div>
          <div className="break-all text-muted">
            imported {settlementFeed.latestImportedBusinessDate ?? "—"} · discovered {settlementFeed.latestDiscoveredBusinessDate ?? "—"}
          </div>
          <div className="break-all text-muted">
            checked {settlementFeed.lastCheckedAt ?? "—"}
          </div>
          {settlementFeed.eccMarksSuppressed && (
            <div className="font-semibold text-negRed">ECC-derived marks suppressed</div>
          )}
        </div>
      </div>
    </details>
  );
}

function ContractCards({ contracts }: { contracts: MarginResult["contracts"] }) {
  return (
    <div className="space-y-2 sm:hidden" aria-label="Per-contract variation margin">
      {contracts.map((contract) => (
        <details
          key={`${contract.code}-${contract.expYear}-${contract.expMonth}`}
          className={cn(
            "group min-w-0 rounded-md border border-border/60",
            contract.expired && "bg-border/10"
          )}
        >
          <summary className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2">
            <span aria-hidden="true" className="shrink-0 text-muted transition-transform group-open:rotate-90">▸</span>
            <span className="min-w-0 flex-1">
              <span className="block break-words font-semibold text-fg">{contract.label}</span>
              <span className="block break-all font-mono text-xs text-muted">
                {contract.code} {contract.expYear}-{String(contract.expMonth).padStart(2, "0")}
                {contract.expired ? " · expired" : ""}
              </span>
            </span>
            <span className={cn("shrink-0 text-right tabular-nums", statusClass(contract.status))}>
              <span className="block">{contract.status === "match" ? "✓" : "✗"}</span>
              <span className={cn("block", Math.abs(contract.diff) > 0.011 && "font-semibold text-negRed")}>
                {eur(contract.diff)} €
              </span>
            </span>
          </summary>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border/40 p-3">
            <Fact label="Net position" value={`${contract.netPos} MW`} />
            <Fact label="Status" value={`${contract.status}${contract.incomplete ? " · gaps" : ""}`} />
            <Fact label="App VM" value={`${eur(contract.cumComputedVm)} €`} />
            <Fact label="Bank VM" value={`${eur(contract.cumBankVm)} €`} />
            <Fact
              label="Difference"
              value={`${eur(contract.diff)} €`}
              className={Math.abs(contract.diff) > 0.011 ? "text-negRed" : undefined}
            />
          </dl>
        </details>
      ))}
    </div>
  );
}

function Fact({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted">{label}</dt>
      <dd className={cn("break-words text-sm font-medium tabular-nums text-fg", className)}>
        {value}
      </dd>
    </div>
  );
}

function DaysTable({ days }: { days: LedgerDay[] }) {
  const [openDate, setOpenDate] = useState<string | null>(null);
  return (
    <>
      <div className="space-y-2 sm:hidden">
        {days.map((day) => {
          const open = openDate === day.date;
          const panelId = `margin-day-${day.date}`;
          return (
            <section key={day.date} className="min-w-0 rounded-md border border-border/60">
              <button
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpenDate(open ? null : day.date)}
                className="flex min-h-11 w-full touch-manipulation items-center gap-3 px-3 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
              >
                <span aria-hidden="true" className="w-4 shrink-0 text-muted">
                  {open ? "▾" : "▸"}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-mono font-semibold text-fg">{day.date}</span>
                  <span className="block text-xs text-muted">cash {day.paymentDate ?? "—"}</span>
                </span>
                <span className={cn("shrink-0 text-right", statusClass(day.status))}>
                  <span className="block">{day.status === "match" ? "✓ match" : "✗ check"}</span>
                  <span
                    className={cn(
                      "block tabular-nums",
                      day.diff != null && Math.abs(day.diff) > 0.011 && "font-semibold text-negRed"
                    )}
                  >
                    {eur(day.diff)} €
                  </span>
                </span>
              </button>
              {open && (
                <div id={panelId} className="min-w-0 space-y-3 border-t border-border/40 p-3">
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <Fact
                      label="App VM"
                      value={`${eur(day.computedTotal)} €${day.computedIncomplete ? " *" : ""}`}
                      className={day.computedTotal < 0 ? "text-negRed" : undefined}
                    />
                    <Fact
                      label="Bank VM"
                      value={`${eur(day.bankTotal)} €`}
                      className={(day.bankTotal ?? 0) < 0 ? "text-negRed" : undefined}
                    />
                    <Fact
                      label="Difference"
                      value={`${eur(day.diff)} €`}
                      className={day.diff != null && Math.abs(day.diff) > 0.011 ? "text-negRed" : undefined}
                    />
                    <Fact label="Contracts" value={`${day.matched} match · ${day.mismatched} check`} />
                  </dl>
                  <div className="space-y-2">
                    {day.contracts.map((contract) => (
                      <ContractDayCard
                        key={`${contract.code}-${contract.expYear}-${contract.expMonth}`}
                        contract={contract}
                      />
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>

      <div className="hidden max-h-[calc(100vh-14rem)] max-w-full overflow-auto rounded-md border border-border/60 sm:block">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-bg text-left">
            <tr>
              <Th>Value date</Th>
              <Th>Cash date</Th>
              <Th className="text-right">App VM €</Th>
              <Th className="text-right">Bank VM €</Th>
              <Th className="text-right">Diff €</Th>
              <Th>Contracts</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <Fragment key={d.date}>
                <tr
                  className="cursor-pointer border-t border-border/40 hover:bg-border/10"
                  onClick={() => setOpenDate(openDate === d.date ? null : d.date)}
                >
                  <Td className="font-mono">{d.date}</Td>
                  <Td className="font-mono text-muted">{d.paymentDate ?? "—"}</Td>
                  <Td className={cn("text-right tabular-nums", d.computedTotal < 0 && "text-negRed")}>
                    {eur(d.computedTotal)}
                    {d.computedIncomplete && <span className="text-muted"> *</span>}
                  </Td>
                  <Td className={cn("text-right tabular-nums", (d.bankTotal ?? 0) < 0 && "text-negRed")}>{eur(d.bankTotal)}</Td>
                  <Td className={cn("text-right tabular-nums", d.diff != null && Math.abs(d.diff) > 0.011 && "text-negRed font-semibold")}>
                    {eur(d.diff)}
                  </Td>
                  <Td className="tabular-nums">
                    <span className="text-posGreen">{d.matched}✓</span>
                    {d.mismatched > 0 && <span className="text-negRed"> {d.mismatched}✗</span>}
                  </Td>
                  <Td className={statusClass(d.status)}>{d.status === "match" ? "✓ match" : "✗ check"}</Td>
                </tr>
                {openDate === d.date && (
                  <tr className="border-t border-border/40 bg-border/5">
                    <td colSpan={7} className="px-3 py-2">
                      <div className="max-w-full overflow-x-auto">
                        <DayDetail day={d} />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ContractDayCard({ contract }: { contract: LedgerContractDay }) {
  return (
    <article
      className={cn(
        "min-w-0 rounded border border-border/40 p-3",
        contract.status === "mismatch" && "bg-negRed/5"
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="break-words font-semibold text-fg">{contract.label}</div>
          <div className="break-all font-mono text-xs text-muted">{contract.code}</div>
        </div>
        <span className={cn("shrink-0", statusClass(contract.status))}>
          {STATUS_LABEL[contract.status]}
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        <Fact label="Position (app/bank)" value={`${contract.prevPos} / ${contract.bankPrevPos ?? "—"}`} />
        <Fact label="Position after" value={`${contract.posAfter}`} />
        <Fact label="Previous price" value={px(contract.prevPrice)} />
        <Fact label="Settlement" value={px(contract.price)} />
        <Fact label="Contract MWh" value={`${contract.contractSize ?? "—"}`} />
        <Fact label="Fills (app/bank)" value={`${contract.tradeCount} / ${contract.bankTradeCount}`} />
        <Fact label="Carry VM" value={`${eur(contract.carryVm)} €`} />
        <Fact label="Trades VM" value={`${eur(contract.tradesVm)} €`} />
        <Fact label="App VM" value={`${eur(contract.computedVm)} €`} />
        <Fact label="Bank VM" value={`${eur(contract.bankVm)} €`} />
        <Fact
          label="Difference"
          value={`${eur(contract.diff)} €`}
          className={contract.diff != null && Math.abs(contract.diff) > 0.011 ? "text-negRed" : undefined}
        />
      </dl>
    </article>
  );
}

function DayDetail({ day }: { day: LedgerDay }) {
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-muted">
        <tr>
          <Th>Contract</Th>
          <Th className="text-right">Pos (app/bank)</Th>
          <Th className="text-right">Prev</Th>
          <Th className="text-right">Settle</Th>
          <Th className="text-right">MWh</Th>
          <Th className="text-right">Carry €</Th>
          <Th className="text-right">Trades €</Th>
          <Th className="text-right">Fills (app/bank)</Th>
          <Th className="text-right">App €</Th>
          <Th className="text-right">Bank €</Th>
          <Th className="text-right">Diff €</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {day.contracts.map((c) => (
          <tr key={`${c.code}-${c.expYear}-${c.expMonth}`} className={cn("border-t border-border/30", c.status === "mismatch" && "bg-negRed/5")}>
            <Td>
              {c.label} <span className="font-mono text-muted">{c.code}</span>
            </Td>
            <Td className="text-right tabular-nums">
              {c.prevPos}
              <span className="text-muted">/{c.bankPrevPos ?? "—"}</span>
            </Td>
            <Td className="text-right tabular-nums">{px(c.prevPrice)}</Td>
            <Td className="text-right tabular-nums">{px(c.price)}</Td>
            <Td className="text-right tabular-nums">{c.contractSize ?? "—"}</Td>
            <Td className="text-right tabular-nums">{eur(c.carryVm)}</Td>
            <Td className="text-right tabular-nums">{eur(c.tradesVm)}</Td>
            <Td className="text-right tabular-nums">
              {c.tradeCount}
              <span className="text-muted">/{c.bankTradeCount}</span>
            </Td>
            <Td className={cn("text-right tabular-nums", (c.computedVm ?? 0) < 0 && "text-negRed")}>{eur(c.computedVm)}</Td>
            <Td className={cn("text-right tabular-nums", (c.bankVm ?? 0) < 0 && "text-negRed")}>{eur(c.bankVm)}</Td>
            <Td className={cn("text-right tabular-nums", c.diff != null && Math.abs(c.diff) > 0.011 && "text-negRed font-semibold")}>
              {eur(c.diff)}
            </Td>
            <Td className={statusClass(c.status)}>{STATUS_LABEL[c.status]}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
